import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import svgIcons from "../../../assets/svg/svgIcons";
import HelpFeaturesVideo from "./helpFeaturesVideo";
import { routeUrls } from "../../../navigation/routeUrls";
import { useFetchArticleByRouteApiQuery } from "../../../api/network/helpApiServices";
import { staticRouterPaths } from "../../../navigation/router";
import { useTranslation } from "react-i18next";

type HelpModalProps = {
  onClose: () => void;
};

const HelpPopup = ({ onClose }: HelpModalProps) => {
  const location = useLocation();
  const { t } = useTranslation("translation", { keyPrefix: "helpSection" });

  //Checking for the longest pathname in available routes which matches
  const currentPath = staticRouterPaths.reduce((longest, path) => {
    if (location.pathname.startsWith(path!) && path!.length > longest!.length) {
      return path;
    }
    return longest;
  }, "");

  const {
    data: helpModalData,
    isFetching: helpDataLoading,
  } = useFetchArticleByRouteApiQuery(currentPath!);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-[2px]">
      <div
        className="absolute bg-white overflow-hidden rounded-3xl shadow-2xl
                    top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-[500px] max-h-[90vh] h-[90vh] mt-7
                    lg:translate-x-0 lg:translate-y-0 lg:top-4 lg:right-4 lg:bottom-4 lg:left-auto lg:w-1/2 lg:max-w-none lg:h-auto lg:max-h-none lg:mt-0"
      >
        <div className="relative flex min-h-0 h-full w-full flex-col overflow-hidden">
          {helpDataLoading ? (
            <>
              <div className="border-b bg-accent-blue-paleO66 px-5 py-4 min-h-16">
                <button
                  className="absolute right-4 top-4 text-4xl text-gray-500 hover:text-black sm:text-3xl"
                  onClick={onClose}
                  type="button"
                >
                  ×
                </button>
              </div>
              <div className={`relative z-10 w-full h-48`}>
                <img src={svgIcons.LoadingAnimation} alt="loading-animation" className="size-16 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div></>
          ) : (
            helpModalData ? (
              <><div className="border-b bg-accent-blue-paleO66 px-5 py-4">
                <button
                  className="absolute right-4 top-4 text-4xl text-gray-500 hover:text-black sm:text-3xl"
                  onClick={onClose}
                  type="button"
                >
                  ×
                </button>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold m-2 pr-12 text-accent-blue-dark">
                  {helpModalData?.title}
                </h2>

                <p className="text-gray-700 text-xs sm:text-sm m-2">
                  {helpModalData?.description}
                </p>
              </div><div className="flex-1 overflow-y-auto bg-gray-100 px-5 py-4">
                  <HelpFeaturesVideo videoData={helpModalData?.help_article_video} />

                  <div>
                    <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-accent-blue-dark mb-4 pl-3">
                      {helpModalData.short_steps?.title}
                    </h3>

                    {helpModalData.short_steps?.is_numbered_bulleting ? (
                      <ol className="space-y-4 pl-3">
                        {helpModalData.short_steps.content.map((item: string, index: number) => (
                          <li key={index} className="flex gap-3 flex-row items-baseline">
                            <span className="flex items-center justify-center w-7 h-7 rounded-md bg-gray-200 text-accent-blue-bright text-xs lg:text-sm font-semibold shrink-0">
                              {index + 1}
                            </span>

                            <div
                              className="text-gray-800 text-sm lg:text-base break-words"
                              dangerouslySetInnerHTML={{ __html: item }} />
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <ul className="space-y-4 pl-3">
                        {helpModalData.short_steps?.content.map((item: string, index: number) => (
                          <li key={index} className="flex gap-3 flex-row items-baseline">
                            <img src={svgIcons.RightArrowGrey} alt="Arrow" className="text-gray-500 ml-2" />

                            <div
                              className="text-gray-800 text-sm lg:text-base break-words"
                              dangerouslySetInnerHTML={{ __html: item }} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div></>
            ) : (
              <>
                <div className="border-b bg-accent-blue-paleO66 px-5 py-4 min-h-16">
                  <button
                    className="absolute right-4 top-4 text-4xl text-gray-500 hover:text-black sm:text-3xl"
                    onClick={onClose}
                    type="button"
                  >
                    ×
                  </button>
                </div><div className="flex-1 overflow-y-auto bg-gray-100 px-5 py-4 flex items-center justify-center text-center leading-relaxed">
                  No Help Article Found! <br/>
                  Please Visit The Full Help Center.
                </div>
              </>
            ))}

          <div className="border-t bg-white px-5 py-4 sm:px-4 sm:py-3 flex gap-10 justify-end mx-6 my-1">
            {helpModalData && <Link
              to={`${routeUrls.dashboardChildren.help}/${helpModalData?.slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="flex items-center gap-2 text-sm lg:text-base font-medium text-accent-blue-dark hover:underline" type="button">
                <img src={svgIcons.DetailedHelCenterIcon} alt="help" className="h-4 w-4" />
                View Detailed Guide
              </button>
            </Link>
            }
            <Link
              to={routeUrls.dashboardChildren.help}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="flex items-center gap-2 text-sm lg:text-base font-medium text-accent-blue-dark hover:underline" type="button">
                <img src={svgIcons.HelpCenterIcon} alt="help" className="h-4 w-4" />
                Full Help Center
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default HelpPopup;
